import isNil from 'lodash/isNil'
import { CentralizedHealthScore, NoRLHealthScore } from 'types'
import { compareOptionalNumbers, SortingPlacement } from 'utils/sortingHelpers'

import { ComponentHealthCondition } from './componentHealthConditions'

/**
 * Gets the numeric priority for health score comparison.
 * Lower numbers indicate worse health (higher priority in sorting).
 * ComponentHealthCondition: VeryPoor (0) > Bad (1) > Fair (2) > Excellent (3)
 * NoRLHealthScore: NotEnoughData (4) > NoInspection (5)
 */
function getHealthScorePriority(healthScore: CentralizedHealthScore): number {
  switch (healthScore) {
    case ComponentHealthCondition.VeryPoor:
      return 0
    case ComponentHealthCondition.Bad:
      return 1
    case ComponentHealthCondition.Fair:
      return 2
    case ComponentHealthCondition.Excellent:
      return 3
    case NoRLHealthScore.NotEnoughData:
      return 4
    case NoRLHealthScore.NoInspection:
      return 5
  }

  // This should never be reached - all enum cases are handled above
  throw new Error(`Unhandled health score: ${healthScore}`)
}

export interface HealthScoreData {
  healthConditionFromYear: CentralizedHealthScore
  minRLFromYear: number | undefined
  percentBelowTMin: number | undefined
  hasInspection: boolean
  riskScore: number | undefined
}

/**
 * Compares two health data objects with comprehensive health score comparison logic.
 * Priority order: health_score > RL > %_below_tmin > has_inspection
 * Returns < 0 if 'a' is worse (should be sorted first), > 0 if 'b' is worse
 */
export function compareHealthData(
  a: HealthScoreData,
  b: HealthScoreData,
  placeUndefined: SortingPlacement = SortingPlacement.After
): number {
  if (!isNil(a.healthConditionFromYear) && !isNil(b.healthConditionFromYear)) {
    if (a.healthConditionFromYear !== b.healthConditionFromYear) {
      return (
        getHealthScorePriority(a.healthConditionFromYear) -
        getHealthScorePriority(b.healthConditionFromYear)
      )
    }
  }

  if (isNil(a.healthConditionFromYear) !== isNil(b.healthConditionFromYear)) {
    return isNil(a.healthConditionFromYear) ? placeUndefined : -placeUndefined
  }

  if (!isNil(a.riskScore) && !isNil(b.riskScore)) {
    if (a.riskScore !== b.riskScore) {
      return b.riskScore - a.riskScore
    }
  }

  if (isNil(a.riskScore) !== isNil(b.riskScore)) {
    return isNil(a.riskScore) ? placeUndefined : -placeUndefined
  }

  if (!isNil(a.minRLFromYear) && !isNil(b.minRLFromYear)) {
    if (Math.max(a.minRLFromYear, 0) !== Math.max(b.minRLFromYear, 0)) {
      return a.minRLFromYear - b.minRLFromYear
    }
  }

  if (isNil(a.minRLFromYear) !== isNil(b.minRLFromYear)) {
    return isNil(a.minRLFromYear) ? placeUndefined : -placeUndefined
  }

  if (!isNil(a.percentBelowTMin) || !isNil(b.percentBelowTMin)) {
    const comparison = compareOptionalNumbers(
      b.percentBelowTMin,
      a.percentBelowTMin,
      SortingPlacement.Before
    )
    if (comparison !== 0) {
      return comparison
    }
  }

  if (a.hasInspection !== b.hasInspection) {
    return a.hasInspection ? -placeUndefined : placeUndefined
  }

  return 0
}

/**
 * Helper function to find the lowest (worst) health score from an array of health scores.
 * Uses compareHealthData directly with mapped properties for consistent sorting logic.
 * Returns the calculated CentralizedHealthScore for the worst health score data.
 */
export function getLowestHealthScore(
  scores: [HealthScoreData, ...HealthScoreData[]]
): CentralizedHealthScore {
  const worstHealthData = scores.toSorted((a, b) =>
    compareHealthData(a, b, SortingPlacement.After)
  )[0]

  return worstHealthData.healthConditionFromYear
}
